// One pending timeout per call site: cleared when a new one is set, and cleared when the component
// unmounts (Session APV). Built for the short-lived confirmations the office UI shows after a click,
// such as "Copied" or "Resent", which reset themselves a couple of seconds later.
//
// Why not a bare setTimeout: an uncleared timer outlives its component. In the browser the late state
// update is silently ignored. But a test that finishes inside the window has its environment torn down
// before the timer fires, and the full suite then reports "window is not defined" against a file that
// passed (Session APS's confirming run, from SettingsPage.tsx). That is noise a later session can
// mistake for a real failure. Setting a new timeout also cancels the old one, so a second click within
// the window keeps its confirmation for the full time, instead of the first click's timer ending it
// early.
//
// The same pattern already lives inline in JobPhotoGallery.tsx, knowledgeUi.tsx and ConfirmButton.tsx;
// this gives it one home for the places that had no clearing at all.
//
// The returned function is stable across renders, so it is safe in a useCallback's dependency list.
import { useCallback, useEffect, useRef } from "react";

export function useSingleTimeout(): (callback: () => void, ms: number) => void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    },
    [],
  );

  return useCallback((callback: () => void, ms: number) => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      callback();
    }, ms);
  }, []);
}
